import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import PositionSizingTable from '../../pos_sizing_table/PositionSizingTable';
import PosSizingScatterPlot from '../../pos_sizing_table/PosSizingScatterPlot';
import PositionSizingAttributeWorkspace from '../../pos_sizing_table/PositionSizingAttributeWorkspace';
import PosSizingContextUpdates from '../../pos_sizing_table/PosSizingContextUpdates';
import {
    WORKSPACE_DIMENSIONS,
    workspacePrimarySplitSx,
    workspaceSectionSx,
} from '../../../styles/workspaceChromeStyles';

export default function DashboardPosSizingSection({
    userUid,
    contextUpdateVersion = 0,
    contextUpdateState,
    onRetryContextUpdate,
    posSizingData,
    industries,
    setPosSizingData,
    posSizingWorkspaceBusy,
    displayUnit,
    maxPosSize,
    setMaxPosSize,
    normalVol,
    setNormalVol,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    onPosSizingSaved,
    handlePosSizingEditingChange,
    handlePosSizingSavingChange,
    handlePosSizingPendingChangesChange,
    posSizingLinkedRowId,
    posSizingLinkedRowRequestId,
    setPosSizingVisualizationRows,
    posSizingImportsControl,
    posSizingVisualizationRows,
    handlePosSizingScatterPointSelect,
    posSizingPublicationBaseRef,
    attributeWorkspaceHydration = null,
    active = true,
    loadError = null,
    onRetryLoad,
    onSyncDraft,
    onStartFromWatchlist,
    sizingDefaultsControl = null,
    sizingSettingsCommandRef = null,
}) {
    const [attributeTargetRecalculating, setAttributeTargetRecalculating] = useState(false);
    const positionSizingTableBusy = Boolean(
        posSizingWorkspaceBusy || attributeTargetRecalculating,
    );
    // Retrying cannot fix a draft that needs Sync Draft, so offer that instead.
    const loadErrorAction = onSyncDraft || onRetryLoad;

    return (
        <Grid2 container sx={workspaceSectionSx} spacing={2}>
            <Grid2 size={{ md: 12 }}>
                {['failed', 'loading_failed', 'waiting_for_data'].includes(contextUpdateState?.phase) ? (
                    <Alert severity={contextUpdateState.phase === 'waiting_for_data' ? 'info' : 'warning'} sx={{ mb: 1 }} action={onRetryContextUpdate ? <Button color="inherit" onClick={onRetryContextUpdate}>Retry</Button> : undefined}>
                        {contextUpdateState.message ?? 'Waiting for complete parent context data before updating Position Sizing.'}
                    </Alert>
                ) : null}
                {loadError ? (
                    <Alert
                        severity={onSyncDraft ? 'warning' : 'error'}
                        sx={{ mb: 2 }}
                        action={loadErrorAction ? (
                            <Button color="inherit" onClick={loadErrorAction} disabled={posSizingWorkspaceBusy}>
                                {onSyncDraft ? 'Sync Draft' : 'Retry loading'}
                            </Button>
                        ) : undefined}
                    >
                        <AlertTitle>
                            {onSyncDraft ? 'Sync Draft to continue' : 'Position Sizing could not load'}
                        </AlertTitle>
                        {loadError}
                    </Alert>
                ) : null}
                <Box sx={workspacePrimarySplitSx}>
                    <PositionSizingTable
                        posSizingData={posSizingData}
                        industries={industries}
                        setPosSizingData={setPosSizingData}
                        loading={positionSizingTableBusy}
                        displayUnit={displayUnit}
                        serverMaxPosSize={maxPosSize}
                        setServerMaxPosSize={setMaxPosSize}
                        serverNormalVol={normalVol}
                        setServerNormalVol={setNormalVol}
                        defaultTargetVolPct={defaultTargetVolPct}
                        defaultTargetVolDollar={defaultTargetVolDollar}
                        defaultScaler={defaultScaler}
                        onPosSizingSaved={onPosSizingSaved}
                        onEditingStateChange={handlePosSizingEditingChange}
                        onSavingStateChange={handlePosSizingSavingChange}
                        onPendingChangesStateChange={handlePosSizingPendingChangesChange}
                        linkedRowId={posSizingLinkedRowId}
                        linkedRowRequestId={posSizingLinkedRowRequestId}
                        onVisualizationRowsChange={setPosSizingVisualizationRows}
                        importsControl={posSizingImportsControl}
                        contextUpdatesControl={<PosSizingContextUpdates userUid={userUid} publicationVersion={contextUpdateVersion} />}
                        toolbarActive={active}
                        wrapInGridItem={false}
                        readOnly={positionSizingTableBusy || Boolean(loadError)}
                        posSizingPublicationBaseRef={posSizingPublicationBaseRef}
                        onStartFromWatchlist={onStartFromWatchlist}
                        sizingDefaultsControl={sizingDefaultsControl}
                        sizingSettingsCommandRef={sizingSettingsCommandRef}
                        watchlistAttributeSnapshot={
                            attributeWorkspaceHydration?.state?.watchlist ?? null
                        }
                    />
                    <PosSizingScatterPlot
                        active={active}
                        rows={posSizingVisualizationRows}
                        loading={positionSizingTableBusy}
                        selectedPointId={posSizingLinkedRowId}
                        onPointSelect={handlePosSizingScatterPointSelect}
                        attributeSnapshot={attributeWorkspaceHydration?.state?.watchlist ?? null}
                        defaultAttributeDefinitionId={
                            attributeWorkspaceHydration?.state
                                ?.target_attribute_definition_id ?? null
                        }
                        minHeight={WORKSPACE_DIMENSIONS.primaryTableHeight}
                    />
                </Box>

                <PositionSizingAttributeWorkspace
                    defaultTargetVolPct={defaultTargetVolPct}
                    defaultTargetVolDollar={defaultTargetVolDollar}
                    defaultScaler={defaultScaler}
                    workspaceBusy={posSizingWorkspaceBusy}
                    setPosSizingData={setPosSizingData}
                    posSizingPublicationBaseRef={posSizingPublicationBaseRef}
                    onPosSizingSaved={onPosSizingSaved}
                    onTargetRecalculationStateChange={setAttributeTargetRecalculating}
                    hydratedWorkspace={attributeWorkspaceHydration}
                    active={active}
                />
            </Grid2>
        </Grid2>
    );
}
